import { Page } from "@/components/common/Page";
import Text from "../common/Text";
import { Eyebrow } from "../common/brand/Eyebrow";
import { Button } from "../ui/button";
import { Download } from "lucide-react";
import AnalyticsDataTable from "./AnalyticsDataTable";
import { useEffect, useState } from "react";
import { downloadFile } from "@/utils/downloadFile";
import MultiSelect from "../common/MultiSelectDropdown";
import Icon from "../common/Icon";
import { VisibilityState } from "@tanstack/react-table";
import AnalyticsCriteria from "./AnalyticsCriteria";
import { format, getDaysInMonth } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useGenerateInsightMutation } from "@/hooks/mutations";
import { useDownloadAnalyticsJobsQuery } from "@/hooks/queries";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import ExportFilter from "./ExportFilter";

const Export = () => {
  const { sendEvent } = useAnalyticsManager();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [search, setSearch] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnOptions, setColumnOptions] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [initialSelectedColumns, setInitialSelectedColumns] = useState<
    string[]
  >([]);

  // Date picker state
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [startMonth, setStartMonth] = useState<number>(new Date().getMonth());
  const [startYear, setStartYear] = useState<number>(new Date().getFullYear());
  const [startDay, setStartDay] = useState<number>(1);
  const [endMonth, setEndMonth] = useState<number>(new Date().getMonth());
  const [endYear, setEndYear] = useState<number>(new Date().getFullYear());
  const [endDay, setEndDay] = useState<number>(new Date().getDate());

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - 2 + i
  );

  const getDaysArray = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(new Date(year, month));
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  const startDays = getDaysArray(startYear, startMonth);
  const endDays = getDaysArray(endYear, endMonth);

  const handleColumnChange = (selected: string[]) => {
    const newColumnVisibility = Object.fromEntries(
      columnOptions.map((option) => [option.id, selected.includes(option.id)])
    );
    setColumnVisibility(newColumnVisibility);
  };
  const [downloadExcel, setDownloadExcel] = useState(false);
  const {
    data: excelData,
    isLoading: isDownloading,
    isSuccess,
  } = useDownloadAnalyticsJobsQuery(
    selectedJobId ? parseInt(selectedJobId) : null,
    {
      enabled: downloadExcel && selectedJobId !== null,
    }
  );
  const { mutate: generateInsight, isPending: isGeneratingInsight } =
    useGenerateInsightMutation({
      onError: (error) => {
        console.log("Error generating insight", error);
      },
    });

  useEffect(() => {
    if (isSuccess && downloadExcel) {
      downloadFile(excelData, `${selectedJobId}.xlsx`);
      setDownloadExcel(false);
    }
  }, [isSuccess, excelData, selectedJobId, downloadExcel]);

  const onDownload = () => {
    setDownloadExcel(true);
  };

  const handleGenerateInsights = () => {
    if (!selectedJobId) return;

    const startDate = new Date(startYear, startMonth, startDay);
    const endDate = new Date(endYear, endMonth, endDay);

    generateInsight({
      jobId: selectedJobId,
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
    });

    setIsDatePickerOpen(false);
  };
  sendEvent("export_screen_viewed");
  // const handleSearch = (value: string) => {
  //   setSearch(value);
  //   sendEvent("export_search_submitted", {
  //     search_query: value,
  //   });
  // };

  return (
    <Page className="flex flex-col gap-4 overflow-x-scroll">
      <div className="flex flex-col gap-10">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <Eyebrow>[ EXPORT · ANALYTICS ]</Eyebrow>
            <h1 className="font-display text-[34px] font-medium leading-[1.05] tracking-[-0.02em] text-brand-text">
              Export.
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <AnalyticsCriteria
              selectedJobId={selectedJobId}
              onJobSelect={setSelectedJobId}
            />

            <Button
              variant="default"
              leftIcon={<Download width={16} height={16} />}
              onClick={onDownload}
              isLoading={isDownloading}
              disabled={!selectedJobId}
            >
              Export Data
            </Button>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-end">
          <div className="flex items-center gap-4">
            {/* <SearchInput value={search} onChange={handleSearch} /> */}
            <ExportFilter
              selectedJobId={selectedJobId}
              selectedFilters={selectedFilters}
              onFiltersChange={setSelectedFilters}
            />
            <div className="flex gap-3">
              {columnOptions.length > 0 && ( // Only show when options exist
                <MultiSelect
                  options={columnOptions}
                  initialSelected={initialSelectedColumns}
                  onChange={handleColumnChange}
                  trigger={
                    <Button
                      variant="outline"
                      leftIcon={<Icon name="edit" size={16} />}
                    >
                      Edit Column
                    </Button>
                  }
                  screen_name="export"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <AnalyticsDataTable
        selectedJobId={selectedJobId}
        selectedFilters={selectedFilters}
        search={search}
        columnVisibility={columnVisibility}
        setColumnOptions={setColumnOptions}
        setInitialSelectedColumns={setInitialSelectedColumns}
      />

      <Dialog open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Select Date Range for Insights</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Text
                  variant="paragraph_p6"
                  className="text-shades-neutral-300"
                >
                  Start Date
                </Text>
                <div className="flex gap-2">
                  <select
                    className="flex h-10 w-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={startMonth}
                    onChange={(e) => setStartMonth(Number(e.target.value))}
                  >
                    {months.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex h-10 w-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={startDay}
                    onChange={(e) => setStartDay(Number(e.target.value))}
                  >
                    {startDays.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex h-10 w-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={startYear}
                    onChange={(e) => setStartYear(Number(e.target.value))}
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Text
                  variant="paragraph_p6"
                  className="text-shades-neutral-300"
                >
                  End Date
                </Text>
                <div className="flex gap-2">
                  <select
                    className="flex h-10 w-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={endMonth}
                    onChange={(e) => setEndMonth(Number(e.target.value))}
                  >
                    {months.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex h-10 w-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={endDay}
                    onChange={(e) => setEndDay(Number(e.target.value))}
                  >
                    {endDays.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex h-10 w-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={endYear}
                    onChange={(e) => setEndYear(Number(e.target.value))}
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDatePickerOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateInsights}
              disabled={!selectedJobId || isGeneratingInsight}
              isLoading={isGeneratingInsight}
            >
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default Export;
